import {
  memo,
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { getPreferredVideo } from "../../Utils/media";

// Import SVG Icons
import RelationShipAdviceSVG from "../../Components/ChatSvg/relationShipAdviceSVG";
import CareerAdviceSVG from "../../Components/ChatSvg/careerAdviceSVG";
import MindSetGuidanceSVG from "../../Components/ChatSvg/mindSetGuidanceSVG";
import WellBeingSVG from "../../Components/ChatSvg/wellBeingSVG";
import MySelfSVG from "../../Components/ChatSvg/mySelfSVG";
import ResumeCoachingSVG from "../../Components/ChatSvg/ResumeCoachingSVG";

// Import Components
import ChatInput from "../../Components/ChatInput";
import ContactSelectionModal from "./ContactSelectionModal";
import CareerAdviceModal from "./CareerAdviceModal";
import DefaultLoader from "../LoadingPage/DefaultLoader";

// Import Services and Providers
import { startQuestions } from "../../Services/Assessment";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { checkUserActivation } from "../../Services/coaching";

import { ReactComponent as Dialogues } from "../../Assets/icon/Dialogues.svg";
import { ReactComponent as Elements } from "../../Assets/icon/Elements.svg";
import { ReactComponent as Group } from "../../Assets/icon/Group.svg";
import { YourElementsEntryButton } from "../../Components/YourElements";
// Import Styles
import "./style.css";
import { getUserFaith } from "../../Services/subscription";
import AddRelationShipChatModal from "./AddRelationShipChatModal";
import { getWelcomeMessage } from "../../Services/welcomeMessge";
import Cookies from "js-cookie";
import { getOnboardingRoute, isOnboardingComplete } from "../../Utils/onboardingNavigation";
import GenericModal from "../../Components/Modal/GenericModal";

// Memoized SVG Icons
const SVGIcons = {
  relationship: memo(RelationShipAdviceSVG),
  career: memo(CareerAdviceSVG),
  mindset: memo(MindSetGuidanceSVG),
  wellbeing: memo(WellBeingSVG),
  myself: memo(MySelfSVG),
  resume: memo(ResumeCoachingSVG),
};

// Helper function to detect Firefox
const isFirefox = () => {
  return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
};

function InitialChat({
  onEnterData,
  chatInstruction,
  latestInProgressSession,
  enableCoaching,
  setShowLiveSessionModal,
  isChatProgress,
  loadLatestChatHistory,
  loading,
  setLoading,
  isLoading,
  setIsLoading,
  setIsPrivacyActivated,
  isPrivacyActivated,
  contactSelectionRelationAdvice,
  coachingMinutes,
  onOpenMinutesModal,
  onOpenCoachingTopics,
  onOpenYourElements,
}) {
  const navigate = useNavigate();
  const {
    setHeaderContent,
    setHeaderContentRightSideContent,
    profileContent,
    beliefSystem,
    setbeliefSystem,
  } = useHeader();

  // State variables
  const [selectedChatType, setSelectedChatType] = useState(null);
  const [showRelationShipContacts, setShowRelationShipContacts] =
    useState(false);
  const [showEditRelationship, setshowEditRelationship] = useState(false);
  const [showCareerAdviceModal, setShowCareerAdviceModal] = useState(false);
  const [selectedRelationShipName, setSelectedRelationShipName] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState(
    "Hi, I'm Aris. I'm here to support your journey to health, fulfillment, and joy."
  );
  const privacyNotice = "This Aris text dialogue is private and untracked. It won’t appear in your history or shape future coaching. ";
  const [finalTranscript, setFinalTranscript] = useState(""); // Store final transcript
  const [isEnterPressed, setIsEnterPressed] = useState(false);
  const [collectedFormDataRelationship, setcollectedFormDataRelationship] =
    useState({});
  const [checkingUserStatus, setCheckingUserStatus] = useState(false);
  const [userStatusError, setUserStatusError] = useState(null);
  const [defaultVoice, setDefaultVoice] = useState(false);

  // WebSocket refs
  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const partialTranscriptRef = useRef(""); // Track partial transcript
  const accumulatedTranscriptRef = useRef(""); // Track accumulated transcript across recording sessions

  const randomInRange = (min, max) => Math.random() * (max - min) + min;
  const profile = localStorage.getItem("profile");

  // UseLayoutEffect for header content
  useLayoutEffect(() => {
    setHeaderContent(true);
    setHeaderContentRightSideContent(true);
  }, []);

  // useEffect for initial API calls and setup
  useEffect(() => {
    const handleApiCalls = async () => {
      try {
        // Call all APIs simultaneously
        const results = await Promise.allSettled([
          getUserFaith(),
        ]);

        // Handle getUserFaith response
        if (results[0].status === "fulfilled") {
          const faithRes = results[0].value;
          setbeliefSystem(faithRes?.payload);
          localStorage.setItem(
            "beliefSystem",
            JSON.stringify(faithRes?.payload)
          );
        }

        // Handle startQuestions response
        const { onboarding_step } = profile;

        // If user has incomplete onboarding, redirect to appropriate step
        if (onboarding_step && !isOnboardingComplete(onboarding_step)) {
          const onboardingRoute = getOnboardingRoute(onboarding_step, profile);
          navigate(onboardingRoute);
          return;
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error in API calls:", error);
        setIsLoading(false);
      }
    };

    if (window.confetti && localStorage.getItem("showConfetti")) {
      window.confetti({
        angle: randomInRange(55, 125),
        spread: randomInRange(1000, 1250),
        particleCount: randomInRange(2000, 2250),
        origin: { x: 1, y: 0.9 },
      });
      window.confetti({
        angle: randomInRange(55, 125),
        spread: randomInRange(1000, 1250),
        particleCount: randomInRange(2000, 2250),
        origin: { x: 0, y: 0.9 },
      });
      setTimeout(() => {
        localStorage.removeItem("showConfetti");
      }, 1000);
    }

    handleApiCalls();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopWebSocketTranscription();
    };
  }, []);

  // Video file selection based on capability (prefer WebM when supported)
  const VideoFile = getPreferredVideo(
    `${process.env.REACT_APP_STATIC_CONTENT}ui-components/Sphere_Loader.webm`,
    `${process.env.REACT_APP_STATIC_CONTENT}ui-components/sphere-ios.mov`
  );

  // Convert audio to PCM format
  const convertFloat32ToInt16 = (buffer) => {
    const int16Array = new Int16Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) {
      int16Array[i] = Math.max(-32768, Math.min(32767, buffer[i] * 32768));
    }
    return int16Array.buffer;
  };

  // Start WebSocket Transcription
  const startWebSocketTranscription = async () => {
    try {
      // Get WebSocket URL from environment variable
      let wsUrl = process.env.REACT_APP_WEBSOCKET_TRANSCRIBE_URL;


      if (!wsUrl) {
        alert(
          "WebSocket URL not configured. Please set REACT_APP_WEBSOCKET_TRANSCRIBE_URL in your environment variables."
        );
        return;
      }

      const token = Cookies.get("idtoken");
      wsUrl += "?token=" + token + "&language_code=en-US&sample_rate=16000";

      setIsConnecting(true);

      // Initialize WebSocket
      socketRef.current = new WebSocket(wsUrl);

      socketRef.current.onopen = async () => {
        setIsConnecting(false);

        try {
          // Setup audio processing
          streamRef.current = await navigator.mediaDevices.getUserMedia({
            audio: {
              sampleRate: 16000,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
            },
          });

          // Create AudioContext with conditional sampleRate based on browser
          const audioContextOptions = {};
          if (!isFirefox()) {
            audioContextOptions.sampleRate = 16000;
          }

          audioContextRef.current = new (window.AudioContext ||
            window.webkitAudioContext)(audioContextOptions);

          sourceRef.current = audioContextRef.current.createMediaStreamSource(
            streamRef.current
          );
          processorRef.current = audioContextRef.current.createScriptProcessor(
            4096,
            1,
            1
          );

          // Process audio data and send to WebSocket
          processorRef.current.onaudioprocess = (event) => {
            if (
              socketRef.current &&
              socketRef.current.readyState === WebSocket.OPEN
            ) {
              const pcmData = event.inputBuffer.getChannelData(0);
              const pcmInt16 = convertFloat32ToInt16(pcmData);
              socketRef.current.send(pcmInt16);
            }
          };

          // Connect audio nodes
          sourceRef.current.connect(processorRef.current);
          processorRef.current.connect(audioContextRef.current.destination);

          setIsRecording(true);
        } catch (audioError) {
          setIsConnecting(false);
          stopWebSocketTranscription();

          let errorMessage = "Failed to access microphone. ";
          if (audioError.name === "NotAllowedError") {
            errorMessage += "Please grant microphone permissions.";
          } else if (audioError.name === "NotFoundError") {
            errorMessage += "No microphone found. Please connect a microphone.";
          } else {
            errorMessage += `Error: ${audioError.message}`;
          }
          alert(errorMessage);
        }
      };

      // Handle incoming transcription messages - UPDATED for proper appending
      socketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const transcriptText = data.transcript || "";
          const isPartial = data.is_partial;

          if (transcriptText.trim()) {
            if (isPartial) {
              // Update partial transcript - combine accumulated + current partial
              partialTranscriptRef.current = transcriptText;
              const combinedText =
                accumulatedTranscriptRef.current +
                (accumulatedTranscriptRef.current ? " " : "") +
                transcriptText;
              setTranscript(combinedText);
            } else {
              // Final transcript - add to accumulated transcript
              accumulatedTranscriptRef.current =
                accumulatedTranscriptRef.current +
                (accumulatedTranscriptRef.current ? " " : "") +
                transcriptText;
              setFinalTranscript(accumulatedTranscriptRef.current);
              setTranscript(accumulatedTranscriptRef.current);
              partialTranscriptRef.current = ""; // Clear partial
            }
          }
        } catch (error) {
          // Fallback: treat as plain text and append to accumulated
          if (typeof event.data === "string" && event.data.trim()) {
            const newText = event.data.trim();
            accumulatedTranscriptRef.current =
              accumulatedTranscriptRef.current +
              (accumulatedTranscriptRef.current ? " " : "") +
              newText;
            setFinalTranscript(accumulatedTranscriptRef.current);
            setTranscript(accumulatedTranscriptRef.current);
          }
        }
      };

      socketRef.current.onerror = (error) => {
        console.error("WebSocket Error:", error);
        console.error("Failed to connect to:", wsUrl);
        setIsConnecting(false);
        alert(
          `Connection error. Please check if the WebSocket server is running at: ${wsUrl}`
        );
        stopWebSocketTranscription();
      };

      socketRef.current.onclose = (event) => {
        setIsConnecting(false);
        // Don't auto-stop recording on close unless it's unexpected
        if (isRecording && event.code !== 1000) {
          stopWebSocketTranscription();
        }
      };
    } catch (error) {
      console.error("Error starting WebSocket transcription:", error);
      setIsConnecting(false);
      alert(`Failed to start recording: ${error.message}`);
      stopWebSocketTranscription();
    }
  };

  // Stop WebSocket Transcription
  const stopWebSocketTranscription = () => {
    try {
      // Send accumulated transcript to chat input when stopping
      if (accumulatedTranscriptRef.current.trim()) {
        // This will update the chat input with the accumulated transcript
        setTranscript(accumulatedTranscriptRef.current.trim());
        setFinalTranscript(accumulatedTranscriptRef.current.trim());
      }

      // Close WebSocket gracefully
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.close(1000, "Recording stopped by user"); // Normal closure
      }

      // Clean up audio context
      if (processorRef.current && sourceRef.current) {
        sourceRef.current.disconnect(processorRef.current);
        processorRef.current.disconnect();
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }

      // Stop media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      setIsRecording(false);
      setIsConnecting(false);

      // Reset refs
      socketRef.current = null;
      audioContextRef.current = null;
      processorRef.current = null;
      sourceRef.current = null;
      streamRef.current = null;
      partialTranscriptRef.current = "";

    } catch (error) {
      console.error("Error stopping recording:", error);
    }
  };

  // Toggle Recording Function
  const toggleRecording = () => {
    if (!isRecording && !isConnecting) {
      // Keep existing transcript - don't clear accumulated transcript
      partialTranscriptRef.current = "";
      // Update accumulated transcript with current transcript value
      if (transcript.trim()) {
        accumulatedTranscriptRef.current = transcript.trim();
      }
      startWebSocketTranscription();
    } else if (isRecording) {
      stopWebSocketTranscription();
    }
  };

  // Clear transcript function (useful when user wants to start fresh)
  const clearTranscript = () => {
    setTranscript("");
    setFinalTranscript("");
    accumulatedTranscriptRef.current = "";
    partialTranscriptRef.current = "";
  };

  // Fetch welcome message on component mount
  useEffect(() => {
    const fetchWelcomeMessage = async () => {
      try {
        const message = await getWelcomeMessage();
        if (message) {
          setWelcomeMessage(message);
        }
      } catch (error) {
        console.error("Failed to load welcome message:", error);
      }
    };

    fetchWelcomeMessage();
  }, []);

  // Chat type selection handler
  const onChatTypeSelection = useCallback(
    (option, index) => {
      if (index !== selectedChatType) {
        setSelectedChatType(index);
        // Pass only the text to keep downstream logic unchanged
        onEnterData(option?.response ?? option);
      }
    },
    [selectedChatType]
  );

  // Contact selection handler
  const onContactSelection = (contact) => {
    console.log(contact, "lkljj");
    if (contact?.type === "addNewContact") {
      setshowEditRelationship(true);
      // Clear relationId from URL when adding a new contact
      try {
        const url = new URL(window.location.href);
        url.pathname = "/chat";
        url.searchParams.delete("relationId");
        window.history.replaceState({}, "", url.toString());
      } catch (_) { }
    } else {
      setSelectedRelationShipName(contact);
      onEnterData({
        type: "initiateRelationship",
        text: "I need relationship advice for ",
        name: contact?.first_name + " " + contact?.last_name,
        profile_pic_url: contact?.profile_pic_url,
        contact_relational_data: contact?.related_data,
        relation_id: contact?._id,
        user1_id: contact?.user1_id,
        user2_id: contact?.user2_id,
      });
      // Ensure URL reflects the selected relationship for deep-linking and refresh
      try {
        const url = new URL(window.location.href);
        url.pathname = "/chat"; // normalize to chat route
        if (contact?._id) url.searchParams.set("relationId", contact._id);
        window.history.replaceState({}, "", url.toString());
      } catch (_) { }
    }
  };

  // Modal close handlers
  const onCloseCarrierAdvise = () => {
    setShowCareerAdviceModal(false);
  };

  const onCloseRelationshipAdvise = () => {
    setShowRelationShipContacts(false);
  };

  // Career goal selection handler
  const handleCareerGoalSelection = (goalTitle) => {
    onEnterData(goalTitle);
    setShowCareerAdviceModal(false);
  };

  const handleEnterKeyPress = () => {
    setIsEnterPressed(true);
  };

  const handleNavigatetoElement = () => {
    window.location.href = "/elements";
  };

  const onAddedRelationShip = (data) => {
    onEnterData(data);
    setshowEditRelationship(false);
  };

  const onAddRealtionClose = () => {
    setshowEditRelationship(false);
  };

  // Handle transcript changes from ChatInput
  const handleTranscriptChange = (value) => {
    setTranscript(value);
    // Update accumulated transcript when user manually edits
    if (!isRecording) {
      setFinalTranscript(value);
      accumulatedTranscriptRef.current = value;
    }
  };

  // Loading state
  if (isLoading) {
    return <DefaultLoader />;
  }

  return (
    <>
      {/* Out of minutes UI - shown above the ORB when minutes are 0 */}
      {coachingMinutes !== null && coachingMinutes <= 0 && (
        <div className="coaching-out-of-minutes-container">
          <div className="coaching-out-of-minutes-left">
            <svg
              className="coaching-out-of-minutes-icon"
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.375" y="0.375" width="29.25" height="29.25" rx="14.625" stroke="white" strokeWidth="0.75" />
              <rect x="10.5059" y="11.1953" width="2" height="7.62069" rx="1" fill="white" />
              <rect x="17.5059" y="11.1953" width="2" height="7.62069" rx="1" fill="white" />
              <rect x="21.0059" y="12.5059" width="2" height="4.48276" rx="1" fill="white" />
              <rect x="7.00586" y="12.9883" width="2" height="4.48276" rx="1" fill="white" />
              <rect x="14.0059" y="8.50586" width="2" height="13" rx="1" fill="white" />
            </svg>
            <div className="coaching-out-of-minutes-text">You're out of voice session minutes.</div>
          </div>
          <button className="chat-coaching-add-minutes-button" onClick={onOpenMinutesModal}>Add Minutes</button>
        </div>
      )}

      <div className="relationship-gif-txt-cmb-cntr">
        <div className="relationship-gif-cntr">
          <video
            autoPlay
            loop
            muted
            playsInline
            webkit-playsinline="true"
            className="relationship-gif"
            src={VideoFile}
          />
        </div>
        <div className="relationship-txt-cntr">
          <div>{isPrivacyActivated ? privacyNotice : welcomeMessage}</div>
        </div>
      </div>
      <div
        className={`relationship-input-box-cntr  ${isEnterPressed ? "chat-bg-active" : ""
          }`}
      >
        <div className="relationship-chat-input-top-container">
          <div className={`relationship-input-cntr`}>
            <div className="rl-input">
              <ChatInput
                onChange={onEnterData}
                value={transcript}
                onEnter={handleEnterKeyPress}
                isChatProgress={isChatProgress}
                loadLatestChatHistory={loadLatestChatHistory}
                changeData={handleTranscriptChange}
                showLastChat={true}
                isRecording={isRecording}
                isConnecting={isConnecting}
                startRecording={toggleRecording}
                isPrivacyActivated={isPrivacyActivated}
                chatPlaceHolder={"How can I support you today?"}
              />
            </div>
          </div>
          <div className="relationship-chat-bottom-container">
            <div className="relationship-chat-bottom-container-left" />
            <div className="relationship-chat-bottom-container-right">
              {/* Incognito toggle removed (product 2026-10-15). */}
            </div>
          </div>
        </div>

        {/* Quick Coaching Topics + Your Elements entries */}
        {(onOpenCoachingTopics || onOpenYourElements) && (
          <div className="quick-actions-row">
            {onOpenYourElements && (
              <YourElementsEntryButton
                onClick={onOpenYourElements}
                sessionType="keyboard"
                className="keyboard-mode"
              />
            )}
            {onOpenCoachingTopics && (
              <button
                className="quick-coaching-topics-btn quick-session-topics-btn keyboard-mode"
                onClick={onOpenCoachingTopics}
              >
                Quick Session Topics
              </button>
            )}
          </div>
        )}
      </div>
      <ContactSelectionModal
        isOpen={showRelationShipContacts}
        onClose={onCloseRelationshipAdvise}
        onContactSelection={onContactSelection}
        contactSelectionRelationAdvice={contactSelectionRelationAdvice}
      />
      {showEditRelationship && (
        <AddRelationShipChatModal
          title={"Add new Relationship"}
          onClose={onAddRealtionClose}
          contact={collectedFormDataRelationship}
          onAddedRelationShip={onAddedRelationShip}
        />
      )}
    </>
  );
}

export default memo(InitialChat);
